/**
 * Sticky bottom banner that shows the current connectivity state and the
 * status of the offline action queue.
 *
 * States:
 *   ONLINE + empty queue          → invisible (renders null)
 *   OFFLINE + empty queue         → "Hors-ligne — vos actions seront sauvegardées"
 *   OFFLINE + N queued            → "Hors-ligne — N action(s) en attente"
 *   ONLINE  + N queued            → "Synchronisation… (k/N)"
 *   ONLINE  + queue just drained  → "✅ Tout est à jour" (fades out after 3s)
 *   FAILED action                 → "⚠️ Action refusée : ..." (sticky toast above the banner)
 */
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, CloudOff, Loader2, RefreshCw, WifiOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { subscribeConnectivity, pingNow } from "../../lib/connectivity";
import { drainOnce, subscribeQueue } from "../../lib/offlineQueue";

export default function OfflineBanner() {
    const [online, setOnline] = useState(true);
    const [queueCount, setQueueCount] = useState(0);
    const [syncing, setSyncing] = useState(false);
    const [justDrained, setJustDrained] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const fadeTimer = useRef(null);

    useEffect(() => subscribeConnectivity(({ online: o }) => setOnline(o)), []);

    useEffect(() => subscribeQueue(({ count, syncing: s, failed_label, failed_message }) => {
        setQueueCount((prev) => {
            if (prev > 0 && count === 0 && online) {
                // Queue just emptied → trigger "✅ Tout est à jour" banner briefly
                setJustDrained(true);
                clearTimeout(fadeTimer.current);
                fadeTimer.current = setTimeout(() => setJustDrained(false), 3000);
            }
            return count;
        });
        setSyncing(!!s);
        if (failed_label) {
            toast.error(`Action refusée : ${failed_label}`, {
                description: failed_message,
                duration: 8000,
            });
        }
    }), [online]);

    async function handleRetry() {
        if (retrying) return;
        setRetrying(true);
        try {
            const ok = await pingNow();
            if (ok) {
                toast.success("Connexion rétablie", {
                    description: "Le serveur PREREC répond à nouveau.",
                    duration: 3000,
                });
                drainOnce();
            } else {
                toast.error("Toujours hors-ligne", {
                    description: "Vérifiez votre connexion ou réessayez dans quelques secondes.",
                    duration: 5000,
                });
            }
        } finally {
            setRetrying(false);
        }
    }

    const showSyncing = online && syncing && queueCount > 0;
    const showOfflineEmpty = !online && queueCount === 0;
    const showOfflineQueue = !online && queueCount > 0;
    const showSynced = online && justDrained;
    const visible = showSyncing || showOfflineEmpty || showOfflineQueue || showSynced;

    if (!visible) return null;

    let palette = "bg-amber-500 text-white";
    let icon = <CloudOff className="h-4 w-4" />;
    let label = "Hors-ligne";
    let detail = "Vos prochaines actions seront sauvegardées localement.";
    if (showSyncing) {
        palette = "bg-shariti-orange text-white";
        icon = <Loader2 className="h-4 w-4 animate-spin" />;
        label = "Synchronisation…";
        detail = `${queueCount} action${queueCount > 1 ? "s" : ""} en cours d'envoi.`;
    } else if (showOfflineQueue) {
        palette = "bg-amber-500 text-white";
        icon = <WifiOff className="h-4 w-4" />;
        label = `${queueCount} action${queueCount > 1 ? "s" : ""} en attente`;
        detail = "Vos actions seront synchronisées dès le retour de la connexion.";
    } else if (showSynced) {
        palette = "bg-emerald-500 text-white";
        icon = <CheckCircle2 className="h-4 w-4" />;
        label = "Tout est à jour";
        detail = "Vos actions hors-ligne ont été synchronisées.";
    }

    return (
        <AnimatePresence>
            <motion.div
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 40, opacity: 0 }}
                transition={{ type: "spring", damping: 22, stiffness: 240 }}
                className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 ${palette} shadow-[0_18px_50px_-12px_rgba(0,0,0,0.35)] rounded-full px-4 py-2 flex items-center gap-3 max-w-[92vw]`}
                data-testid="offline-banner"
            >
                {icon}
                <div className="min-w-0">
                    <div className="text-[12px] font-extrabold leading-tight">{label}</div>
                    <div className="text-[11px] opacity-90 leading-tight truncate">{detail}</div>
                </div>
                {(showOfflineEmpty || showOfflineQueue) && (
                    <button
                        onClick={handleRetry}
                        disabled={retrying}
                        data-testid="offline-banner-retry"
                        className="ml-1 inline-flex items-center gap-1 rounded-full bg-white/15 hover:bg-white/25 disabled:opacity-60 px-2 py-1 text-[11px] font-bold transition"
                    >
                        {retrying
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <RefreshCw className="h-3 w-3" />} {retrying ? "Test en cours…" : "Réessayer"}
                    </button>
                )}
            </motion.div>
        </AnimatePresence>
    );
}
